/* eslint-disable max-lines */
/* eslint-disable max-statements */
import { useRef, useState } from 'react'
import { ApolloError, useLazyQuery, useReactiveVar } from '@apollo/client'
import type { Adapter } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { User } from '__generated__/graphql'
import { GET_WALLET_CHALLENGE } from 'api/auth/get-wallet-challenge'
import { GET_USER_BY_ADDRESS } from 'api/general/get-user-by-address'
import { featureFlags } from 'app/app-feature-flags'
import { useQueryParams } from 'shared/hooks/use-query'
import { useResponsive } from 'shared/hooks/use-responsive'
import { globalOverlayLoadingVar } from 'shared/store/global-overlay-state-store'
import { isUserAuthLoadingVar } from 'shared/store/user'
import {
  wizardFlowTypeVar,
  wizardModeVar,
  wizardStepVar
} from '../store/wizard.store'
import {
  connectSelectedSolanaWallet,
  getWalletErrorMessage,
  isUserRejectedWalletError,
  signChallengeWithAdapter
} from '../utils/connect-solana-adapter'
import {
  applyUserLookupResult,
  keepConnectWalletDialogOpen,
  notifyOnWalletError,
  notifyOnWalletInfo,
  notifySignFailure,
  openWizardAfterSign,
  storeWalletSignature
} from '../utils/connect-solana-wallet-flow'
import type { SolanaConnectorProvider } from '../utils/get-solana-connector-providers'
import { notifyOnChallengeError } from '../utils/notify'
import { resetWizardState } from '../utils/reset-wizard-state'

const { isMainNetFeature } = featureFlags

export const useConnectSolanaWallet = () => {
  const [isMobile] = useResponsive('xs')
  const isConnectingRef = useRef(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [getUserByAddress, { loading: userDataLoading }] =
    useLazyQuery(GET_USER_BY_ADDRESS)
  const [getWalletChallenge, { loading: queryLoading }] =
    useLazyQuery(GET_WALLET_CHALLENGE)
  const referralCode = useQueryParams().get('referralCode') || ''
  const wizardMode = useReactiveVar(wizardModeVar)
  const wizardFlowType = useReactiveVar(wizardFlowTypeVar)
  const wizardStep = useReactiveVar(wizardStepVar)
  const { select, disconnect, wallets } = useWallet()

  const startConnecting = () => {
    if (isConnectingRef.current) return false
    isConnectingRef.current = true
    setIsConnecting(true)
    return true
  }

  const stopConnecting = () => {
    isConnectingRef.current = false
    setIsConnecting(false)
  }

  const connectProviderWallet = (provider: SolanaConnectorProvider) =>
    connectSelectedSolanaWallet(wallets, provider.walletName, select)

  const signConnectedWallet = async (
    adapter: Adapter,
    walletAddress: string
  ) => {
    const { data } = await getWalletChallenge({
      variables: { walletAddress },
      fetchPolicy: 'network-only',
      onError: (e: ApolloError) => notifyOnChallengeError(e)
    })
    if (!data?.getWalletChallenge) throw new Error('No challenge')

    const signature = await signChallengeWithAdapter(
      adapter,
      data.getWalletChallenge.challenge
    )
    if (!signature) {
      throw new Error('Cannot connect to your wallet. Please, retry')
    }

    storeWalletSignature(signature, walletAddress)
  }

  const handleConnectionFailure = (error: unknown) => {
    resetWizardState(wizardMode)
    disconnect()
    if (!isUserRejectedWalletError(error)) {
      notifyOnWalletInfo(getWalletErrorMessage(error))
    }
  }

  const signAndContinue = async (
    provider: SolanaConnectorProvider,
    onSigned: (walletAddress: string) => Promise<void> | void,
    onSignError: (error: unknown) => void
  ) => {
    const connected = await connectProviderWallet(provider)

    try {
      await signConnectedWallet(connected.adapter, connected.walletAddress)
      await onSigned(connected.walletAddress)
    } catch (error) {
      onSignError(error)
    }
  }

  const handleConnection = async (provider: SolanaConnectorProvider) => {
    if (!provider || !startConnecting()) return undefined

    try {
      return await connectProviderWallet(provider)
    } catch (error) {
      handleConnectionFailure(error)
      return undefined
    } finally {
      stopConnecting()
    }
  }

  const handleAuthWallet = async (provider: SolanaConnectorProvider) => {
    if (isMainNetFeature) {
      await handleConnectWalletMainNet(provider)
      return
    }
    if (!startConnecting()) return

    try {
      await signAndContinue(
        provider,
        () => openWizardAfterSign(wizardMode, wizardFlowType, wizardStep),
        error => {
          notifyOnWalletError(getWalletErrorMessage(error))
          disconnect()
        }
      )
    } catch (error) {
      handleConnectionFailure(error)
    } finally {
      stopConnecting()
    }
  }

  const lookupUserAfterSign = async (walletAddress: string) => {
    if (isMobile) globalOverlayLoadingVar(true)

    const queryUserData = await getUserByAddress({
      variables: { address: walletAddress },
      fetchPolicy: 'network-only',
      onCompleted: () => isMobile && globalOverlayLoadingVar(false),
      onError: () => isMobile && globalOverlayLoadingVar(false)
    })

    applyUserLookupResult(
      queryUserData.data?.getUserByAddress as User,
      walletAddress,
      referralCode
    )
    keepConnectWalletDialogOpen()
  }

  const handleConnectWalletMainNet = async (
    provider: SolanaConnectorProvider
  ) => {
    if (!startConnecting()) return
    isUserAuthLoadingVar(true)

    try {
      await signAndContinue(provider, lookupUserAfterSign, error =>
        notifySignFailure(error, disconnect)
      )
    } catch (error) {
      handleConnectionFailure(error)
    } finally {
      stopConnecting()
      isUserAuthLoadingVar(false)
    }
  }

  return {
    handleAuthWallet,
    handleConnectWallet: handleConnection,
    loading: queryLoading || userDataLoading || isConnecting
  }
}
